import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Modal, InputField, Select, SelectOption, Button } from '@ets/design-system';

import { useDispatch, useSelector } from '_common/hooks/redux';
import { toast } from 'utils/toast';

import { closeAndResetModal } from '_common/modals/ModalsSlice';
import {
  useGetElementStatusListQuery,
  useDeleteElementStatusMutation,
  selectOrderedElementStatusList,
} from '_common/services/api/elementStatusApi';

import styles from './DeleteElementStatusModal.module.scss';

const MESSAGES = {
  header: 'DELETE_ELEMENT_STATUS',
  body: 'DELETE_ELEMENT_STATUS_TEXT',
};

const DeleteElementStatusModal = () => {
  const intl = useIntl();
  const dispatch = useDispatch();

  useGetElementStatusListQuery();
  const [deleteElementStatus] = useDeleteElementStatusMutation();
  const isOpen = useSelector((state) => state.modals.open.DeleteElementStatusModal);
  const id = useSelector((state) => state.modals.DeleteElementStatusModal.id);
  const list = useSelector(selectOrderedElementStatusList);
  const status = list.find((status) => id === status.id);
  // local state
  const [replacement, setReplacement] = useState<SelectOption | null>(null);

  useEffect(() => {
    if (isOpen && list.length > 0) {
      setReplacement({ value: list[0].id, label: list[0].name });
    }
  }, [isOpen, list]);

  const submit = async () => {
    if (replacement?.value) {
      const payload = { id, status: replacement.value };
      await deleteElementStatus(payload);
      toast({
        variant: 'success',
        title: 'ELEMENT_STATUS_DELETED_TITLE',
        message: 'ELEMENT_STATUS_DELETED_DESCRIPTION',
        messageValues: { name: status?.name ?? '' },
      });
      close();
    }
  };
  const close = () => {
    setReplacement(null);
    dispatch(closeAndResetModal('DeleteElementStatusModal'));
  };
  return (
    <Modal open={!!isOpen} width="63.5rem" close={close} testId="delete-element-status">
      <Modal.Header>
        <FormattedMessage id={MESSAGES.header} />
      </Modal.Header>
      <Modal.Body style={{ overflow: 'visible' }}>
        <FormattedMessage
          id={MESSAGES.body}
          values={{
            // @ts-expect-error component prop type out of date
            d: (chunks: React.ReactChild | null | undefined) => (
              <div className={styles.text}>{chunks}</div>
            ),
            name: status?.name,
          }}
        />
        <InputField
          size="l"
          style={{ width: '100%', margin: '2rem 0 0 0' }}
          label={intl.formatMessage({ id: 'CHANGE_STATUS_TO' })}
          testId="change-status-to"
        >
          <Select
            variant="standard"
            isClearable
            size="l"
            width="100%"
            options={list.map((status) => ({
              value: status.id,
              label: status.name,
            }))}
            onChange={setReplacement}
            value={replacement}
            filterOption={({ value }: { value: string }) => value !== id}
            useBodyAsPortal
            testId="change-status-to"
          />
        </InputField>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="standard" size="m" onClick={close} testId="delete-element-status-cancel">
          <FormattedMessage id="global.cancel" />
        </Button>
        <Button
          size="m"
          variant="danger"
          disabled={!replacement}
          onClick={submit}
          testId="delete-element-status-submit"
          style={{ margin: '0 0 0 1rem' }}
        >
          <FormattedMessage id={MESSAGES.header} />
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DeleteElementStatusModal;
